
import { IRetirementSavingsPersonInputs } from 'app/calcs/retirementSavings'
import { analyzeSavings, getChartData } from 'app/calcs/RetirementSavingsAnalyzer'
import { RetirementAgeSelector } from 'app/components/form/RetirementAgeSelector'
import { Infobox } from 'app/components/Infobox'
import MaterialNumber from 'app/components/material/MaterialNumber'
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { getPersonalWithdrawalChart } from 'app/containers/PipelineSection/Retirement/charts'
import { dateAtAge, IRetirementPerson, Profile } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import { TSavingsCalculator } from 'app/workbookDAOs/SavingsWorkbookDAO'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap'
import * as ReactHighcharts from 'react-highcharts'

const debug = utils.makeDebug(false)

interface IContributions {
  annualContributionToAllDcAccounts: number
  ageContributionsStop: moment.Duration
  withdrawalAge: moment.Duration
}

interface IWithdrawal {
  monthlyWithdrawalAmount: number
}

interface ISavingsWithdrawalBaseState extends IContributions, IWithdrawal {
  chartConfig: any
  personFirstNamePossessive: string
  analysis: any
  lastWithdrawalDate: moment.Moment
  profile: Profile
}

const log = utils.makeDebug(true)

class SavingsWithdrawalBase extends QuestionsGroupContainer<IQuestionsGroupContainerProps, ISavingsWithdrawalBaseState> {
  annualContributionsAmountNS = 'retirement.{person}.annualContributions.amount'
  ageContributionsStopWhenNS = 'retirement.{person}.annualContributions.stopWhen'
  withdrawalsMonthlyAmountNS = 'retirement.{person}.withdrawals.monthlyAmount'
  withdrawalsStartWhenNS = 'retirement.{person}.withdrawals.startWhen'

  state: ISavingsWithdrawalBaseState = {
    annualContributionToAllDcAccounts: 0,
    ageContributionsStop: undefined,
    withdrawalAge: undefined,
    monthlyWithdrawalAmount: 0,
    chartConfig: {},
    personFirstNamePossessive: 'Person',
    analysis: null,
    lastWithdrawalDate: null,
    profile: null
  }

  person: IRetirementPerson
  personKind: string
  savingsInputs: IRetirementSavingsPersonInputs
  setupChart = _.memoize(getPersonalWithdrawalChart, JSON.stringify)

  setup(personKind: string) {
    this.personKind = personKind
    this.annualContributionsAmountNS = this.annualContributionsAmountNS.replace('{person}', '{' + personKind + '}')
    this.ageContributionsStopWhenNS = this.ageContributionsStopWhenNS.replace('{person}', '{' + personKind + '}')
    this.withdrawalsMonthlyAmountNS = this.withdrawalsMonthlyAmountNS.replace('{person}', '{' + personKind + '}')
    this.withdrawalsStartWhenNS = this.withdrawalsStartWhenNS.replace('{person}', '{' + personKind + '}')

    this.state.annualContributionToAllDcAccounts = this.getValueForQuestion(this.annualContributionsAmountNS)
    this.state.ageContributionsStop = this.getValueForQuestion(this.ageContributionsStopWhenNS)
    this.state.withdrawalAge = this.getValueForQuestion(this.withdrawalsStartWhenNS)
    this.state.monthlyWithdrawalAmount = this.getValueForQuestion(this.withdrawalsMonthlyAmountNS) || 0
  }

  updateSavings(profile: Profile) {
    if (!profile) {
      // tslint:disable-next-line:no-console
      console.log(this.personKind + 'Withdrawal- updateSavings called but no profile!')
      return
    }

    this.savingsInputs.annualContributions.amount = this.state.annualContributionToAllDcAccounts
    this.savingsInputs.annualContributions.stopWhen = this.state.ageContributionsStop
    this.savingsInputs.withdrawals.startWhen = this.state.withdrawalAge
    this.savingsInputs.withdrawals.monthlyAmount = this.state.monthlyWithdrawalAmount

    const st = moment.utc()
    const ratesOfReturn = profile.ratesOfReturn
    const req = {
      member: profile.getHeadOfHousehold(),
      memberInputs: profile.getRetirementForHeadOfHouseHold(),
      spouse: profile.getSpouse(),
      spouseInputs: profile.getRetirementForSpouse(),
      ratesOfReturn
    }
    analyzeSavings(req)
      .then((analyses) => {
        //log('analysis: ' + JSON.stringify(analyses))
        const analysis = (this.person === profile.getHeadOfHousehold()) ? analyses.member : analyses.spouse
        if (!analysis) { return }
        const chartConfig = this.setupChart(analysis)

        const lastWithdrawal = analysis.lastWithdrawalInfo
        if (lastWithdrawal) {
          const lastWithdrawalDate = lastWithdrawal.when
          this.setState({ chartConfig, analysis, lastWithdrawalDate })
        } else {
          // tslint:disable-next-line:no-console
          //console.log(this.personKind + 'withdrawal- no lastWithdrawal found')
          const lastWithdrawalDate: moment.Moment = null
          this.setState({ chartConfig, analysis, lastWithdrawalDate })
        }
      })
  }

  update<K extends keyof ISavingsWithdrawalBaseState>(key: K, value: any) {
    const newState = { ...this.state }
    newState[key] = value

    this.markSignificantProfileUpdate(this.state.profile)

    this.setState(newState, () => this.updateSavings(this.state.profile))
  }

  updateAgeContributionsStop(ageContributionsStop: moment.Duration) {
    this.update('ageContributionsStop', ageContributionsStop)
  }

  updateContributions<C extends keyof IContributions>(key: C, e: React.ChangeEvent<HTMLInputElement>, convertToISOString = false) {
    const value = e.currentTarget.value

    if (convertToISOString) {
      const valueDuration = this.numberStrToYearDuration(value)
      this.update(key, valueDuration)
    } else {
      const valueNum = this.toNumber(value)
      this.update(key, valueNum)
    }
  }

  updateWithdrawal<C extends keyof IWithdrawal>(key: C, e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.currentTarget.value
    const valueNum = this.toNumber(value)

    log(`updateWithdrawal: "${valueNum} type: ${typeof valueNum}`)
    if (typeof valueNum === 'number') {
      this.update(key, valueNum)
    }
  }

  renderAccountBalance() {
    if (!this.state.analysis.withdrawals) { return null }
    const balanceAtFirstWithdrawal = this.state.analysis.balanceAsOf(this.state.analysis.withdrawals.startWithdrawals)
    if (balanceAtFirstWithdrawal) {
      const x = utils.toCurrency(balanceAtFirstWithdrawal)
      return (
        <div>
          Account balance at {this.state.personFirstNamePossessive} withdrawal age:&nbsp;
        <a>{x}</a>
        </div>
      )
    }
  }

  renderHelpfulInformation() {
    const noAnalysis = utils.isNullOrUndefined(this.state.analysis)
    const noProfile = utils.isNullOrUndefined(this.state.profile)
    if (noAnalysis || noProfile) {
      // tslint:disable-next-line:no-console
      debug('Withdrawal- missing analysis? ' + noAnalysis + ', or missing profile? ' + noProfile)
      return null
    }

    const showInfo = this.state.analysis.savings.totalSavings
    if (!showInfo) { return null }

    return <Col md={6} className="text-center">
      <h4 className="animated fadeIn">
        {this.renderAccountBalance()}
        {this.renderWithdrawalInfo()}
      </h4>
      <ReactHighcharts className="animated fadeIn"
        config={this.state.chartConfig} />
      <p><small><b>Tip:</b> You can hover over data points to get more info.</small></p>
    </Col>
  }

  renderWithdrawalInfo() {
    const lastWithdrawalDateText = !!this.state.lastWithdrawalDate ? this.state.lastWithdrawalDate.format('MMMM, YYYY') : undefined
    // tslint:disable-next-line:no-console
    //console.log('lastWithdrawalDateText is ' + lastWithdrawalDateText)
    const levelWithdrawalAmount = this.state.analysis.levelWithdrawalAmount

    return (
      <span>
        {!!levelWithdrawalAmount && <article>
          Level withdrawal amount:&nbsp;
          <a>{utils.toCurrency(levelWithdrawalAmount)}</a>
        </article>}
        {!!lastWithdrawalDateText && <article>
          Last withdrawal is:&nbsp;
          <a>{lastWithdrawalDateText}</a>
        </article>}
      </span>
    )
  }

  render() {
    const bStyle = {
      marginTop: '-10px'
    }

    return (
      <section>
        <h1>Withdrawing From {this.state.personFirstNamePossessive} Savings</h1>
        <h3 className="lead">What are your future plans for your savings?</h3>
        <Row>
          <Col md={6}>
            <MaterialNumber
              anyPlaceholder={
                <span>
                  What will the annual contribution to all accounts be?
                  <Infobox>This "annual contribution" is a total of all monies contributed by you and/or your employer to the various accounts on the prior page.</Infobox>
                </span>}
              style={bStyle}
              formattedNumberMode
              name={this.annualContributionsAmountNS}
              value={this.state.annualContributionToAllDcAccounts}
              onChange={(e) => this.updateContributions('annualContributionToAllDcAccounts', e)} />

            <RetirementAgeSelector min={55} max={70}
              placeholder="At what age do contributions stop?"
              selectedValue={this.state.ageContributionsStop}
              onChange={(a) => this.update('ageContributionsStop', a)} />

            <RetirementAgeSelector min={55} max={70}
              anyPlaceholder={
                <span>When will withdrawals begin?
                    <Infobox>Under law, withdrawals from a retirement savings plan (including IRA accounts) prior to age 59 1/2 will result in additional taxes / penalities.  You may want to consult with an investment professional to discuss your pattern of withdrawals prior to commencement.</Infobox>
                </span>}
              selectedValue={this.state.withdrawalAge}
              onChange={(a) => this.update('withdrawalAge', a)} />

            <MaterialNumber
              style={bStyle}
              anyPlaceholder={
                <span>What amount will you withdraw each month?
                  <Infobox>
                    The amount that will be paid to you each month from your combined accounts.
                    &nbsp;For illustration purposes we have assumed you will withdraw the same amount each year.
                    <br /><br />The “Level withdrawal amount” shown on the right is the amount that you could withdrawal
                    &nbsp;each month to have your savings last for the rest of your expected lifetime (as selected in your profile).
                    &nbsp;This amount can give you a starting point for a “reasonable” monthly withdrawal amount.
                  </Infobox>
                </span>}
              formattedNumberMode
              name={this.withdrawalsMonthlyAmountNS}
              value={this.state.monthlyWithdrawalAmount}
              onChange={(e) => this.updateWithdrawal('monthlyWithdrawalAmount', e)} />
            {(!this.state.monthlyWithdrawalAmount) && <div className="animated fadeIn alert-info">
              <small>&nbsp; Hmm, you haven't entered a withdrawal amount...</small>
            </div>}
          </Col>

          {this.renderHelpfulInformation()}

        </Row>
      </section>
    )
  }
}

export {
  SavingsWithdrawalBase
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Retirement/SavingsWithdrawalBase/index.tsx